import { css, theme } from "twin.macro";

/**
 * NOTE: Keep this in sync with the (custom) Tailwind theme `screens` config.
 * @see https://tailwindcss.com/docs/breakpoints
 */
export type Screen = "sm" | "md" | "lg" | "xl" | "2xl" | "max";
export const screens = theme<Record<Screen, string>>`screens`;

// The maximum value is calculated as the minimum of the next one less 0.02px.
// @see https://www.w3.org/TR/mediaqueries-4/#mq-min-max
function getNextBpValue(bp: string) {
  return `${parseInt(bp) - 0.02}px`;
}

export function up(bp: Screen) {
  const screen = screens[bp];
  return `@media only screen and (min-width: ${screen})`;
}

export function down(bp: Screen) {
  const screen = getNextBpValue(screens[bp]);
  return `@media only screen and (max-width: ${screen})`;
}

export function between(bpMin: Screen, bpMax: Screen) {
  const screenMin = screens[bpMin];
  const screenMax = getNextBpValue(screens[bpMax]);
  return `@media only screen and (min-width: ${screenMin}) and (max-width: ${screenMax})`;
}

export function only(bp: Screen) {
  const screenKeys = Object.keys(screens) as Screen[];
  const currentKeyIndex = screenKeys.indexOf(bp);
  const nextBp = screenKeys[currentKeyIndex + 1];
  return nextBp ? between(bp, nextBp) : up(bp);
}

export type ScreenProp<T extends string | number> = T | `${Screen}:${T}` | `${string} ${Screen}:${T}`;
export type ScreenProps = Partial<Record<Screen | "DEFAULT", string>>;
export type ScreenQuery = { screen: Screen; match: string; query: typeof css };

/**
 * Creates a mapper of media queries.
 *
 * @param props The screen props to use.
 * @returns A list of screen queries.
 */
export function screenQueries<V extends ScreenProps>(props: V): ScreenQuery[] {
  const screenEntries = Object.entries(screens);

  return screenEntries
    .filter(([screen]) => screen in props)
    .map(([screen, width]) => {
      return {
        screen,
        match: props[screen as Screen],
        query: (...args: [never]) => css`
          @media only screen and (min-width: ${width}) {
            ${css(...args)}
          }
        `,
      } as ScreenQuery;
    });
}

/**
 * Parses a screen prop into a key-value pair.
 * Uses the `DEFAULT` key for values without a screen prefix.
 *
 * @param prop The screen prop to parse.
 * @returns An object with the screen prop parsed into a key-value pair.
 */
export function screenProps<T extends string | number>(prop: ScreenProp<T>): ScreenProps {
  const props = String(prop)
    .split(" ")
    .map((v) => {
      const [key, val] = v.split(":");
      return val ? [key, val] : ["DEFAULT", key];
    });

  return Object.fromEntries(props);
}
